import { useEffect } from 'react';
import { Pressable } from 'react-native';
import { XStack, YStack } from 'tamagui';

import { Text } from '@/components/text';
import { PrimaryButton } from '@/components/primary-button';
import { borderWidth } from '@/theme/tokens.generated';
import { hit } from '@/lib/hit-slop';

// Issue #150 Task 4. Grepped this codebase for any existing transient-
// notification pattern (toast/snackbar/banner) before designing --
// found none; every real hit was a comment referencing the CONCEPT
// (threads/index.tsx's incoming-reveal-request callout calls itself
// "the banner" in a comment) or an inline callout that's actually
// persistent, state-driven UI (safety-reminder's warning block,
// block-user/report-target's success confirmation) -- not a transient,
// self-dismissing overlay. This is genuinely new UI vocabulary for this
// app, not a dedup, so it's kept minimal per this task's own scope:
// message + optional action + auto-dismiss + manual dismiss, nothing
// more (no queueing/stacking system -- no real call site needs one yet,
// and this project's own "don't invent states/mechanisms a primitive
// has no use for" precedent, see EmptyState's own comment, applies
// here too).
//
// Same 4-variant feedback-token set safety-reminder/threads/block-user/
// report-target already use for their own persistent callouts
// ($feedbackWarningBg/Border/Text etc.) -- `info` added to the THEME in
// this same commit (tamagui.config.shared.ts), since the raw
// tokens/colors.json tokens already existed but were never wired past
// tokens.generated.ts into either theme object; nothing reached for
// `$feedbackInfo*` before this component.
export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface ToastAction {
  label: string;
  onPress: () => void;
}

export interface ToastProps {
  variant: ToastVariant;
  message: string;
  action?: ToastAction;
  // Always required, not optional -- a toast with no way to close it
  // (auto-dismiss disabled, no manual X) would trap its own message on
  // screen forever. The manual dismiss button below always calls this;
  // `durationMs` is a SEPARATE, optional auto-dismiss on top of it.
  onDismiss: () => void;
  // Undefined = stays until manually dismissed or `action` is taken.
  // No default duration is picked here -- the right auto-dismiss length
  // depends on the message's own length/urgency (a caller reading this
  // file's own docs decides per-call-site, matching this project's
  // "deliberately does NOT own the copy" precedent from EmptyState:
  // this component doesn't own timing decisions either).
  durationMs?: number;
}

const VARIANT_TOKENS: Record<ToastVariant, { bg: string; border: string; text: string }> = {
  success: { bg: '$feedbackSuccessBg', border: '$feedbackSuccessBorder', text: '$feedbackSuccessText' },
  error: { bg: '$feedbackErrorBg', border: '$feedbackErrorBorder', text: '$feedbackErrorText' },
  warning: { bg: '$feedbackWarningBg', border: '$feedbackWarningBorder', text: '$feedbackWarningText' },
  info: { bg: '$feedbackInfoBg', border: '$feedbackInfoBorder', text: '$feedbackInfoText' },
};

export function Toast({ variant, message, action, onDismiss, durationMs }: ToastProps) {
  useEffect(() => {
    if (durationMs === undefined) return;
    const id = setTimeout(onDismiss, durationMs);
    return () => clearTimeout(id);
    // onDismiss is expected to be a fresh closure per render from most
    // callers (an inline arrow function clearing state); re-arming the
    // timer on every such render would mean a toast whose parent
    // re-renders for an unrelated reason never actually auto-dismisses.
    // Re-arming only when the visible DURATION or MESSAGE changes
    // matches what a caller actually means by "show this toast for N ms".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durationMs, message]);

  const tokens = VARIANT_TOKENS[variant];

  return (
    <YStack
      role="alert"
      backgroundColor={tokens.bg}
      borderColor={tokens.border}
      borderWidth={borderWidth.hairline}
      borderRadius="$sm"
      p="$4"
      gap="$2"
    >
      <XStack alignItems="flex-start" justifyContent="space-between" gap="$3">
        <Text color={tokens.text} flex={1} fontWeight="$semibold">
          {message}
        </Text>
        {/* Plain react-native Pressable, not a Tamagui-styled element --
            same reasoning as Card's own comment: a Pressable is the
            proven-correct way to get real DOM/native button semantics
            plus keyboard focus in this codebase, confirmed by this
            project's own earlier finding (Skeleton's a11y fix, this
            same plan) that Tamagui's own components don't reliably
            forward RN-native accessibility props on web. */}
        <Pressable accessibilityRole="button" {...hit(8)} onPress={onDismiss}>
          <Text color={tokens.text} fontWeight="$semibold">
            ×
          </Text>
        </Pressable>
      </XStack>
      {action && (
        <PrimaryButton size="sm" onPress={action.onPress}>
          {action.label}
        </PrimaryButton>
      )}
    </YStack>
  );
}
